// WHAT: API client for Miracle Flow backend commands
// WHY:  Provides type-safe interface for all Tauri backend commands
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: core.invoke from Tauri API, types
// RISKS:
//   - NOTE: Backend commands may not exist - will fail at runtime
//   - NOTE: Error handling is minimal - may not be user-friendly

import type {
  Session,
  SessionStatus,
  Affirmation,
  AffirmationType,
  AffirmationCreateRequest,
  JournalEntry,
  MorningScribingRequest,
  EveningReflectionRequest,
  JournalStats,
  FileInfo,
  TimerAudioInfo,
  TauriResult,
} from "../types";

import { core } from "@tauri-apps/api";

/**
 * WHAT:
 *   API client that provides type-safe functions for all Miracle Flow backend
 *   commands organized by feature area.
 *
 * WHY:
 *   Centralizes backend communication and provides consistent interface for
 *   all Tauri commands.
 *
 * BEHAVIOR:
 *   - Organizes commands by feature (session, affirmation, journal, etc.)
 *   - Provides type-safe function signatures
 *   - Uses core.invoke for all backend calls
 *
 * RISKS:
 *   - NOTE: Backend commands may not exist - will fail at runtime
 *   - NOTE: Error handling is minimal - may not be user-friendly
 */

// NOTE: Session API - session management commands
export const sessionApi = {
  cleanupDuplicateSessions: (): TauriResult<void> => core.invoke("cleanup_duplicate_sessions"),

  completeStep: (step: string): TauriResult<void> => core.invoke("complete_step", { step }),

  finishSession: (): TauriResult<Session> => core.invoke("finish_session"),

  getCurrentSession: (): TauriResult<null | Session> => core.invoke("get_current_session"),

  getSessionDuration: (): TauriResult<null | number> => core.invoke("get_session_duration"),

  getSessionProgress: (): TauriResult<[number, number] | null> =>
    core.invoke("get_session_progress"),

  getSessionStatus: (): TauriResult<SessionStatus> => core.invoke("get_session_status"),

  hasActiveSession: (): TauriResult<boolean> => core.invoke("has_active_session"),

  loadActiveSession: (): TauriResult<null | Session> => core.invoke("load_active_session"),

  startEveningSession: (): TauriResult<string> => core.invoke("start_evening_session"),

  startMorningSession: (): TauriResult<string> => core.invoke("start_morning_session"),

  stopSession: (): TauriResult<Session> => core.invoke("stop_session"),
};

// Affirmation API
export const affirmationApi = {
  batchCreateAffirmations: (affirmations: AffirmationCreateRequest[]): TauriResult<void> =>
    core.invoke("batch_create_affirmations", { affirmations }),

  createAffirmation: (text: string, affirmationType: AffirmationType): TauriResult<Affirmation> => {
    // Mappe 'morning' -> 'Morning', 'evening' -> 'Evening'
    const tauriType = affirmationType.charAt(0).toUpperCase() + affirmationType.slice(1);
    return core.invoke("create_affirmation", {
      req: { affirmationType: tauriType, text },
    });
  },

  deleteAffirmation: (id: string): TauriResult<void> => core.invoke("delete_affirmation", { id }),

  getEveningAffirmations: (): TauriResult<Affirmation[]> => core.invoke("get_evening_affirmations"),

  getMorningAffirmations: (): TauriResult<Affirmation[]> => core.invoke("get_morning_affirmations"),

  getRandomEveningAffirmation: (): TauriResult<Affirmation | null> =>
    core.invoke("get_random_evening_affirmation"),

  getRandomMorningAffirmation: (): TauriResult<Affirmation | null> =>
    core.invoke("get_random_morning_affirmation"),

  updateAffirmation: (id: string, newText: string): TauriResult<void> =>
    core.invoke("update_affirmation", { id, newText }),
};

// Journal API
export const journalApi = {
  createJournalEntry: (date: string): TauriResult<JournalEntry> =>
    core.invoke("create_journal_entry", { date }),

  deleteJournalEntry: (id: string): TauriResult<void> =>
    core.invoke("delete_journal_entry", { id }),

  getJournalEntriesRange: (startDate: string, endDate: string): TauriResult<JournalEntry[]> =>
    core.invoke("get_journal_entries_range", { endDate, startDate }),

  getJournalEntryByDate: (date: string): TauriResult<JournalEntry | null> =>
    core.invoke("get_journal_entry_by_date", { date }),

  getJournalStats: (): TauriResult<JournalStats> => core.invoke("get_journal_stats"),

  getJournalStreak: (): TauriResult<number> => core.invoke("get_journal_streak"),

  getRecentJournalEntries: (limit: number): TauriResult<JournalEntry[]> =>
    core.invoke("get_recent_journal_entries", { limit }),

  getTodaysJournalEntry: (): TauriResult<JournalEntry | null> =>
    core.invoke("get_todays_journal_entry"),

  saveEveningReflection: (request: EveningReflectionRequest): TauriResult<void> =>
    core.invoke("save_evening_reflection", { request }),

  saveMorningScribing: (request: MorningScribingRequest): TauriResult<void> =>
    core.invoke("save_morning_scribing", { request }),

  searchJournalEntries: (searchTerm: string): TauriResult<JournalEntry[]> =>
    core.invoke("search_journal_entries", { searchTerm }),

  updateJournalEntry: (request: {
    id: string;
    content?: string;
    date?: string;
  }): TauriResult<void> => core.invoke("update_journal_entry", { request }),
};

// Media API
export const mediaApi = {
  deleteVisualization: (fileName: string): TauriResult<void> =>
    core.invoke("delete_visualization", { fileName }),

  getAppDataDir: (): TauriResult<string> => core.invoke("get_app_data_dir"),

  getAvailableTimerSounds: (): TauriResult<TimerAudioInfo[]> =>
    core.invoke("get_available_timer_sounds"),

  openFileDialog: (): TauriResult<null | string> => core.invoke("open_file_dialog"),

  playTimerSound: (soundName: string): TauriResult<void> =>
    core.invoke("play_timer_sound", { soundName }),

  uploadVisualization: (request: { file_path: string }): TauriResult<string> =>
    core.invoke("upload_visualization", { request }),

  validateFile: (filePath: string): TauriResult<FileInfo> =>
    core.invoke("validate_file", { filePath }),
};
